import { inject, injectable } from 'inversify';
import axiosRetry from 'axios-retry';
import ConfigService from '../../config/ConfigService';
import mapSignalingApiError from '../../errors/signalingApiErrorMapper';
import { TOKEN } from '../../inversify.tokens';
import { Api } from './api';

export type DatacenterType = 'p2p' | 'common';

export interface SignalingSecurityData {
  token?: string;
  peerApiVersion?: number;
  datacenterType?: DatacenterType;
  preferredDC?: string;
}

type SignalingErrorListener = (error: Error) => void;

const REQUEST_TIMEOUT_IN_MS = 7_000;
const RETRY_COUNT = 3;
const RETRY_BASE_DELAY_IN_MS = 300;
const RETRY_MAX_DELAY_IN_MS = 2_000;
const RETRY_MAX_JITTER_IN_MS = 300;
const RETRY_EXPONENTIAL_FACTOR = 2;
const REQUEST_ABORTED_ERROR_CODE = 'ERR_CANCELED';
const REQUEST_TIMEOUT_ERROR_CODE = 'ECONNABORTED';
const RETRY_EXCLUDED_ROUTE_PARTS = ['/ping', '/ice-parameters'];

const isRetryExcludedRoute = (url?: string): boolean =>
  typeof url === 'string' && RETRY_EXCLUDED_ROUTE_PARTS.some((routePart) => url.includes(routePart));

@injectable()
export default class SignalingApiClient extends Api<SignalingSecurityData> {
  private readonly errorListeners = new Set<SignalingErrorListener>();

  private abortController = new AbortController();

  constructor(@inject(TOKEN.Config) private readonly config: ConfigService) {
    super({
      baseURL: String(process.env.LIVEDIGITAL_SIGNALING_SERVER_BASE_URL),
      securityWorker: (security) => {
        if (!security) {
          return;
        }

        const headers: Record<string, string> = {};

        if (security.token) {
          headers.Authorization = `Bearer ${ security.token }`;
        }

        if (security.peerApiVersion) {
          headers['x-peer-api-version'] = String(security.peerApiVersion);
        }

        if (security.datacenterType) {
          headers['x-datacenter-type'] = security.datacenterType;
        }

        if (security.preferredDC) {
          headers['x-datacenter-name'] = security.preferredDC;
        }

        return { headers };
      },
    });

    this.instance.interceptors.request.use((config) => {
      if (!config.signal && this.abortController) {
        config.signal = this.abortController.signal;
      }

      return config;
    });

    axiosRetry(this.instance, {
      retries: RETRY_COUNT,
      shouldResetTimeout: true,
      retryDelay: (retryCount) => {
        const base = Math.min(
          RETRY_BASE_DELAY_IN_MS * (RETRY_EXPONENTIAL_FACTOR ** (retryCount - 1)),
          RETRY_MAX_DELAY_IN_MS,
        );

        return base + Math.random() * RETRY_MAX_JITTER_IN_MS;
      },
      retryCondition: (error) => {
        if (error.code === REQUEST_ABORTED_ERROR_CODE) {
          return false;
        }

        if (isRetryExcludedRoute(error.config?.url)) {
          return false;
        }

        return axiosRetry.isNetworkError(error) || (error.code === REQUEST_TIMEOUT_ERROR_CODE && !error.response);
      },
    });

    this.instance.interceptors.response.use((response) => response, (error) => {
      const mappedError = mapSignalingApiError(error);
      this.notifyErrorListeners(mappedError);
      throw mappedError;
    });

    this.instance.defaults.timeout = REQUEST_TIMEOUT_IN_MS;

    const signalingServerUrl = this.config.get('signalingServerUrl');

    if (signalingServerUrl) {
      this.instance.defaults.baseURL = signalingServerUrl;
    }
  }

  abortPendingRequests(reason?: string): void {
    this.abortController.abort(reason);
    this.abortController = new AbortController();
  }

  addErrorListener(listener: SignalingErrorListener): () => void {
    this.errorListeners.add(listener);

    return () => {
      this.errorListeners.delete(listener);
    };
  }

  private notifyErrorListeners(error: Error): void {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (listenerError) {
        void listenerError;
      }
    });
  }
}
